import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import usePersistTripChatRecentSearch from 'tripPlanner/hooks/usePersistTripChatRecentSearch'
import { buildExperienceRecentSearchUpsertBody } from 'tripPlanner/utils/tripChatRecentSearchPayload'
import Places from 'constants/places'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { addQueryParamsToPath, buildUrl } from 'lib/url/searchUrlUtils'
import { replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import { validateExtractedCheckInOrCheckOutDate } from 'lib/search/searchUtils'
import useExperienceSearchUrl from 'hooks/Experiences/useExperienceSearchUrl'
import { usePlaceByName } from 'hooks/usePlace'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import { resolveSearchDefaults } from 'tripPlanner/utils/resolveSearchDefaults'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { buildExperienceSearchSubmittedActivityEntry } from './buildExperienceSearchSubmittedActivityEntry'
import { buildLastExperienceSearchCommittedStateEntry } from './buildLastExperienceSearchCommittedStateEntry'
import type { DrawerSortValue, ListingDrawerSearchSubmitParams } from '../ListingDrawerSearchControls'
import { getConcreteExperienceSearchPlace } from './experienceSearchUrlUtils'

export interface ExperienceDateRange {
  checkIn?: string
  checkOut?: string
}

interface UseExperienceListingSearchStateOptions {
  isOpen: boolean
  placeName: string
  placeCountry?: string
  placeId?: string
  seedSearchFromPlace?: boolean
  stopCoordinates?: { lat: number; lng: number }
  contextStop?: TripStop
  itineraryData?: TripItineraryData
  regionCode: string
  onOpenReset?: () => void
}

function resolveExperienceSort(value: string | undefined): DrawerSortValue {
  switch (value) {
    case 'type':
    case 'price.asc':
    case 'price.desc':
    case 'latest':
    case 'endingsoon':
    case 'recommended':
    case 'bestValue':
    case 'customerRating':
    case 'mostBooked':
    case 'mostViewed':
      return value
    default:
      return 'type'
  }
}

function normalizeSearchValue(value?: string) {
  return value?.trim().toLowerCase()
}

function createDestinationSearchItem(value: string, label: string): App.SearchDestination {
  return {
    searchType: 'destination',
    value,
    format: {
      mainText: label,
    },
  }
}

function isSearchDestination(searchItem?: App.SearchItem): searchItem is App.SearchDestination {
  return searchItem?.searchType === 'destination' && searchItem.value !== Places.Anywhere.id
}

function getValidExperienceDateRange(dateRange: ExperienceDateRange): ExperienceDateRange {
  const validatedCheckIn = validateExtractedCheckInOrCheckOutDate(dateRange.checkIn)
  if (!validatedCheckIn) return {}

  const checkIn = moment(validatedCheckIn, ISO_DATE_FORMAT, true)
  const validatedCheckOut = validateExtractedCheckInOrCheckOutDate(dateRange.checkOut)
  const checkOut = validatedCheckOut ? moment(validatedCheckOut, ISO_DATE_FORMAT, true) : checkIn

  return {
    checkIn: checkIn.format(ISO_DATE_FORMAT),
    checkOut: checkOut.isValid() && !checkOut.isBefore(checkIn, 'day') ?
        checkOut.format(ISO_DATE_FORMAT) :
        checkIn.format(ISO_DATE_FORMAT),
  }
}

function getDefaultExperienceDateRange(contextStop?: TripStop): ExperienceDateRange {
  const datedDays = contextStop?.days
    .map(day => day.date)
    .filter((date): date is string => !!date) ?? []
  const defaultStartDate = contextStop?.startDate ?? datedDays[0]
  const defaultEndDate = contextStop?.endDate ?? datedDays[datedDays.length - 1] ?? defaultStartDate

  return getValidExperienceDateRange({
    checkIn: defaultStartDate,
    checkOut: defaultEndDate,
  })
}

function getTripDateRange(itineraryData?: TripItineraryData) {
  const dates = itineraryData?.stops.flatMap((stop) => {
    return [
      stop.startDate,
      stop.endDate,
      ...stop.days.map(day => day.date),
    ]
  }).filter((date): date is string => !!date) ?? []

  if (!dates.length) return undefined

  const sortedDates = [...dates].sort()
  const validRange = getValidExperienceDateRange({
    checkIn: sortedDates[0],
    checkOut: sortedDates[sortedDates.length - 1],
  })

  if (!validRange.checkIn || !validRange.checkOut) return undefined

  return {
    start: validRange.checkIn,
    end: validRange.checkOut,
  }
}

/**
 * The drawer is usually opened for a stop, which always carries both halves.
 * Opened for a bare place -- a breadcrumb, say -- there is no country to add,
 * and a trailing ", " would be searched as part of the destination.
 */
function buildExperienceSearchQuery(placeName: string, placeCountry?: string): string {
  return [placeName, placeCountry].filter(Boolean).join(', ')
}

export default function useExperienceListingSearchState({
  isOpen,
  placeName,
  placeCountry,
  placeId,
  seedSearchFromPlace,
  stopCoordinates,
  contextStop,
  itineraryData,
  regionCode,
  onOpenReset,
}: UseExperienceListingSearchStateOptions) {
  const eventBus = useTripPlannerEventBusOptional()
  const { chatId } = useChatStreamStatus()
  const persistRecentSearch = usePersistTripChatRecentSearch(chatId)
  const [searchLocation, setSearchLocation] = useState(buildExperienceSearchQuery(placeName, placeCountry))
  const [selectedSearchItem, setSelectedSearchItem] = useState<App.SearchItem | undefined>(undefined)
  const [selectedDateRange, setSelectedDateRange] = useState<ExperienceDateRange>(() => getDefaultExperienceDateRange(contextStop))
  const [sortValue, setSortValue] = useState<DrawerSortValue>('type')
  const wasOpenRef = useRef(false)
  const scopeKey = `${chatId}:${contextStop?.id ?? 'unscoped'}:${placeName}:${placeCountry ?? ''}`
  const [initializedScope, setInitializedScope] = useState<string>()
  const [pendingSearch, setPendingSearch] = useState<{ scopeKey: string, token: symbol }>()
  const defaultSearchQuery = buildExperienceSearchQuery(placeName, placeCountry)
  const searchQuery = searchLocation.trim() || defaultSearchQuery
  const defaultDateRange = useMemo(() => getDefaultExperienceDateRange(contextStop), [contextStop])
  const resolvedOpenDefaults = useMemo(() => {
    const resolved = resolveSearchDefaults({
      surface: 'experiences',
      stopId: contextStop?.id ?? null,
      recentSearches: itineraryData?.recentSearches,
      indicativeSearchMonth: itineraryData?.indicativeSearchMonth,
      // A place-opened drawer is *about* its place, so the location is already
      // chosen and outranks the recent search. Dates and sort still come from
      // it -- those carry over between places, the destination does not.
      selected: seedSearchFromPlace
        ? { locationQuery: defaultSearchQuery, placeId }
        : undefined,
      concrete: {
        checkIn: defaultDateRange.checkIn,
        checkOut: defaultDateRange.checkOut,
        locationQuery: defaultSearchQuery,
      },
    })

    return resolved.surface === 'experiences' ? resolved : undefined
  }, [
    contextStop?.id,
    defaultDateRange.checkIn,
    defaultDateRange.checkOut,
    defaultSearchQuery,
    itineraryData?.indicativeSearchMonth,
    itineraryData?.recentSearches,
    placeId,
    seedSearchFromPlace,
  ])
  const previousDefaultsRef = useRef({
    checkIn: defaultDateRange.checkIn,
    checkOut: defaultDateRange.checkOut,
    searchQuery: defaultSearchQuery,
  })
  const searchLocationRef = useRef(searchLocation)
  searchLocationRef.current = searchLocation
  const tripDateRange = useMemo(() => getTripDateRange(itineraryData), [itineraryData])

  useEffect(() => {
    if (isOpen && (!itineraryData || itineraryData.tripChatId !== chatId)) return
    if (isOpen && (!wasOpenRef.current || initializedScope !== scopeKey)) {
      setPendingSearch(undefined)
      setInitializedScope(scopeKey)
      onOpenReset?.()
      const seededLocation = resolvedOpenDefaults?.locationQuery ?? defaultSearchQuery
      setSearchLocation(seededLocation)
      setSelectedSearchItem(
        resolvedOpenDefaults?.placeId
          ? createDestinationSearchItem(resolvedOpenDefaults.placeId, seededLocation)
          : undefined,
      )
      setSelectedDateRange(getValidExperienceDateRange(resolvedOpenDefaults?.dateRange ?? defaultDateRange))
      setSortValue(resolveExperienceSort(resolvedOpenDefaults?.sort))
      wasOpenRef.current = true
      previousDefaultsRef.current = {
        checkIn: defaultDateRange.checkIn,
        checkOut: defaultDateRange.checkOut,
        searchQuery: defaultSearchQuery,
      }
      return
    }

    if (!isOpen) {
      wasOpenRef.current = false
      setInitializedScope(undefined)
      setPendingSearch(undefined)
      previousDefaultsRef.current = {
        checkIn: defaultDateRange.checkIn,
        checkOut: defaultDateRange.checkOut,
        searchQuery: defaultSearchQuery,
      }
      return
    }

    const previousDefaults = previousDefaultsRef.current
    const searchQueryChanged =
      normalizeSearchValue(previousDefaults.searchQuery) !== normalizeSearchValue(defaultSearchQuery)
    const datesChanged =
      previousDefaults.checkIn !== defaultDateRange.checkIn ||
      previousDefaults.checkOut !== defaultDateRange.checkOut

    if (searchQueryChanged &&
      normalizeSearchValue(searchLocationRef.current) === normalizeSearchValue(previousDefaults.searchQuery)
    ) {
      setSearchLocation(defaultSearchQuery)
      setSelectedSearchItem(undefined)
    }

    if (datesChanged) {
      setSelectedDateRange((currentDateRange) => ({
        checkIn: currentDateRange.checkIn === previousDefaults.checkIn
          ? defaultDateRange.checkIn
          : currentDateRange.checkIn,
        checkOut: currentDateRange.checkOut === previousDefaults.checkOut
          ? defaultDateRange.checkOut
          : currentDateRange.checkOut,
      }))
    }

    previousDefaultsRef.current = {
      checkIn: defaultDateRange.checkIn,
      checkOut: defaultDateRange.checkOut,
      searchQuery: defaultSearchQuery,
    }
  }, [chatId, defaultDateRange, defaultSearchQuery, initializedScope, isOpen, itineraryData, onOpenReset, resolvedOpenDefaults, scopeKey])

  const [resolvedExperienceSearchUrl] = useExperienceSearchUrl({
    latLong: {
      latitude: stopCoordinates?.lat,
      longitude: stopCoordinates?.lng,
    },
    placeLevels: ['multi_city_vicinity', 'province_state', 'city'],
  })
  const resolvedExperienceSearchPlace = useMemo(
    () => getConcreteExperienceSearchPlace(resolvedExperienceSearchUrl),
    [resolvedExperienceSearchUrl],
  )
  const resolvedPlaceByName = usePlaceByName(searchQuery)
  const selectedDestination = isSearchDestination(selectedSearchItem) ? selectedSearchItem : undefined
  const placeByNameId = resolvedPlaceByName?.id && resolvedPlaceByName.id !== Places.Anywhere.id ?
    resolvedPlaceByName.id :
    undefined
  const searchQueryMatchesDefault = normalizeSearchValue(searchQuery) === normalizeSearchValue(defaultSearchQuery)
  const resolvedPlaceByNameDestination = placeByNameId ?
      createDestinationSearchItem(placeByNameId, resolvedPlaceByName?.name ?? searchQuery) :
    undefined
  const resolvedCoordinateDestination = resolvedExperienceSearchPlace ?
      createDestinationSearchItem(resolvedExperienceSearchPlace.placeId, resolvedExperienceSearchPlace.name) :
    undefined
  let resolvedDestination = selectedDestination ?? resolvedPlaceByNameDestination
  if (!resolvedDestination && searchQueryMatchesDefault) {
    resolvedDestination = resolvedCoordinateDestination
  }
  const resolvedPlaceId = resolvedDestination?.value
  const fallbackPlaceIds = resolvedCoordinateDestination && resolvedCoordinateDestination.value !== resolvedPlaceId ?
      [resolvedCoordinateDestination.value] :
    undefined
  // Refined = diverged from open seed (recent/concrete), not from stop defaults alone.
  const openBaselineLocation = resolvedOpenDefaults?.locationQuery ?? defaultSearchQuery
  const openBaselineDateRange = getValidExperienceDateRange(
    resolvedOpenDefaults?.dateRange ?? defaultDateRange,
  )
  const openBaselineSort = resolveExperienceSort(resolvedOpenDefaults?.sort)
  const searchQueryMatchesOpenBaseline =
    normalizeSearchValue(searchQuery) === normalizeSearchValue(openBaselineLocation)
  const isLocationOrDateRefined = !searchQueryMatchesOpenBaseline ||
    selectedDateRange.checkIn !== openBaselineDateRange.checkIn ||
    selectedDateRange.checkOut !== openBaselineDateRange.checkOut
  const isRefinedSearch = isLocationOrDateRefined || sortValue !== openBaselineSort
  const hasUnresolvedDestinationSearch = !searchQueryMatchesDefault && !!searchQuery.trim() && !resolvedPlaceId

  useEffect(() => {
    if (!isOpen || itineraryData?.tripChatId !== chatId || initializedScope !== scopeKey || hasUnresolvedDestinationSearch || !resolvedPlaceId) return
    persistRecentSearch(buildExperienceRecentSearchUpsertBody({
      tripStopId: contextStop?.id ?? null,
      searchQuery,
      placeId: resolvedPlaceId,
      dateRange: selectedDateRange,
      sort: sortValue,
    }))
  }, [chatId, contextStop?.id, hasUnresolvedDestinationSearch, initializedScope, isOpen, itineraryData?.tripChatId, persistRecentSearch, resolvedPlaceId, scopeKey, searchQuery, selectedDateRange, sortValue])

  const acceptSearch = useCallback((query: string, destination: App.SearchItem | undefined, dates: ExperienceDateRange, sort: DrawerSortValue) => {
    const effectiveDates = getValidExperienceDateRange(dates)
    wasOpenRef.current = true
    setInitializedScope(scopeKey)
    setSearchLocation(query)
    setSelectedSearchItem(destination)
    setSelectedDateRange(effectiveDates)
    setSortValue(sort)
    // A fresh token per acceptance, so a repeated identical search is still its own
    // occurrence rather than depending on some other value happening to change.
    setPendingSearch({ scopeKey, token: Symbol('accepted search') })
  }, [scopeKey])

  // Resolution may finish after submission. Only publish the current resolved intent.
  useEffect(() => {
    if (!isOpen || initializedScope !== scopeKey || !resolvedPlaceId || pendingSearch?.scopeKey !== scopeKey) return
    setPendingSearch(undefined)
    eventBus?.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildExperienceSearchSubmittedActivityEntry(searchQuery, selectedDateRange, sortValue)],
    })
    eventBus?.dispatch({
      type: 'trip_planner_committed_state',
      entries: [buildLastExperienceSearchCommittedStateEntry(contextStop?.id, searchQuery, selectedDateRange, sortValue)],
    })
  }, [contextStop?.id, eventBus, initializedScope, isOpen, pendingSearch, resolvedPlaceId, scopeKey, searchQuery, selectedDateRange, sortValue])

  const handleSearch = useCallback((query: string, destination?: App.SearchItem) => {
    acceptSearch(query, destination, selectedDateRange, sortValue)
  }, [acceptSearch, selectedDateRange, sortValue])

  const handleSearchSubmit = useCallback((params: ListingDrawerSearchSubmitParams) => {
    const dates = params.hotelDateSelection.type === 'specific'
      ? { checkIn: params.hotelDateSelection.checkIn, checkOut: params.hotelDateSelection.checkOut }
      : {}
    acceptSearch(params.searchValue, params.searchItem, dates, params.sortValue ?? sortValue)
  }, [acceptSearch, sortValue])

  const handleDateChange = useCallback((dates: ExperienceDateRange) => {
    const effectiveDates = getValidExperienceDateRange(dates)
    if (effectiveDates.checkIn === selectedDateRange.checkIn && effectiveDates.checkOut === selectedDateRange.checkOut) return
    acceptSearch(searchQuery, selectedSearchItem, effectiveDates, sortValue)
  }, [acceptSearch, searchQuery, selectedDateRange, selectedSearchItem, sortValue])

  const applySort = useCallback((sort: DrawerSortValue) => {
    if (sort === sortValue) return
    acceptSearch(searchQuery, selectedSearchItem, selectedDateRange, sort)
  }, [acceptSearch, searchQuery, selectedDateRange, selectedSearchItem, sortValue])

  const browseAllResultsUrl = useMemo(() => {
    const searchParams = {
      from: selectedDateRange.checkIn,
      to: selectedDateRange.checkOut,
      sortBy: sortValue === 'type' ? undefined : sortValue,
    }

    if (selectedDestination) {
      return buildUrl(
        `/search/experiences?${replaceExperienceQueryString({
          destinationId: selectedDestination.value,
          destinationName: selectedDestination.format.mainText,
          ...searchParams,
        })}`,
        regionCode,
      )
    }

    if (resolvedPlaceByName?.id && resolvedPlaceByName.id !== Places.Anywhere.id) {
      return buildUrl(
        `/search/experiences?${replaceExperienceQueryString({
          destinationId: resolvedPlaceByName.id,
          destinationName: resolvedPlaceByName.name,
          ...searchParams,
        })}`,
        regionCode,
      )
    }

    if (resolvedExperienceSearchPlace) {
      return buildUrl(addQueryParamsToPath(resolvedExperienceSearchUrl, searchParams), regionCode)
    }

    return buildUrl('/experiences', regionCode)
  }, [regionCode, resolvedExperienceSearchPlace, resolvedExperienceSearchUrl, resolvedPlaceByName, selectedDateRange.checkIn, selectedDateRange.checkOut, selectedDestination, sortValue])

  return {
    isSearchSessionReady: isOpen && initializedScope === scopeKey,
    browseAllResultsUrl,
    fallbackPlaceIds,
    handleDateChange,
    handleSearch,
    handleSearchSubmit,
    hasUnresolvedDestinationSearch,
    isLocationOrDateRefined,
    isRefinedSearch,
    resolvedDestination,
    resolvedExperienceSearchPlace,
    resolvedPlaceId,
    searchLocation,
    searchQuery,
    selectedDateRange,
    applySort,
    sortValue,
    tripDateRange,
  }
}
